import currency from "currency.js";
import { AnimatePresence } from "framer-motion";
import moment from "moment";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { InstrumentTypeResponse } from "../../../codegen-api";
import { COLORS, TEXT_COLORS } from "../../../constants/design/colors";
import { FUNDING_RATE_PRECISION } from "../../../constants/precision/form";
import { IPerpsMarket } from "../../../contexts/MarketInstrumentContext/useGetMarkets";
import { useFunding } from "../../../hooks/api/funding/useFunding";
import { useGetMarketSummary } from "../../../hooks/api/marketSummary/useGetMarketSummary";
import { useGetStatistics } from "../../../hooks/api/statistics/useGetStatistics";
import { IStat } from "../../../interfaces/Toast";
import { AssetResponse } from "../../../utils/asset";
import { nanosToSeconds, timeLeftToExpiry } from "../../../utils/date";
import { getContractPriceStep } from "../../../utils/instruments";
import IndexPriceStat from "./IndexPriceStat";
import Mark24hChange from "./Mark24hChange";
import MarkPrice from "./MarkPrice";
import OpenInterestStat from "./OpenInterestStat";
import Stat from "../Stat";
import TakerBuySellVolumeIndicator from "../TakerBuySellVolumeIndicator";
import {
  OtherStatsWrapper,
  OtherStatsWrapperParent,
  TradingHeaderWrapper,
} from "../style";

export interface IPerpsTradingHeaderProps {
  perpInstrument?: IPerpsMarket;
  isMobileScreen?: boolean;
  isTradeForm?: boolean;
}

function PerpsTradingHeader({
  perpInstrument,
  isMobileScreen,
  isTradeForm,
}: IPerpsTradingHeaderProps) {
  const { t } = useTranslation("app", {
    keyPrefix: "TradingHeader.PerpsTradingHeader",
  });

  const { data: statsData } = useGetStatistics(
    perpInstrument?.underlying_asset as AssetResponse,
    InstrumentTypeResponse.Perpetual
  );

  const { data: marketsSummary } = useGetMarketSummary();

  const { price_precision, amount_precision } =
    getContractPriceStep(perpInstrument);

  const { data: fundingData, mutate: mutateFunding } = useFunding(
    perpInstrument?.instrument_name
  );
  const [fundingCountdown, setFundingCountdown] = useState("00m:00s");

  // Funding rate countdown and auto mutate when reached 0
  useEffect(() => {
    const int = setInterval(() => {
      if (
        fundingData &&
        Object.keys(fundingData).length > 0 &&
        !perpInstrument?.pre_launch
      ) {
        const nextTime = moment.unix(nanosToSeconds(fundingData.next_epoch));
        const { minutes, seconds } = timeLeftToExpiry(nextTime.unix());

        if (minutes <= 0 && seconds <= 0) {
          setFundingCountdown("00m:00s");
          mutateFunding();
        } else {
          setFundingCountdown(
            `${minutes < 10 ? `0${minutes}` : minutes}m:${
              seconds < 10 ? `0${seconds}` : seconds
            }s`
          );
        }
      }
    }, 1000);
    return () => clearInterval(int);
  }, [fundingData, mutateFunding, perpInstrument?.pre_launch]);

  const assetMark24hAgo = useMemo(() => {
    if (marketsSummary && perpInstrument?.underlying_asset) {
      const assetSummary = marketsSummary.summaries?.find(
        (summary) =>
          summary.asset === (perpInstrument.underlying_asset as AssetResponse)
      );
      if (assetSummary) {
        return assetSummary.perpetual_info?.mark_price_24h_ago;
      }
    }
    return undefined;
  }, [marketsSummary, perpInstrument?.underlying_asset]);

  const stats: IStat[] = useMemo(() => {
    const fundingRate: IStat = {
      label: t("hourly_funding_rate"),
      value: fundingData
        ? `${Number(fundingData.funding_rate) >= 0 ? "+" : ""}${currency(
            Number(fundingData.funding_rate) * 100,
            { precision: FUNDING_RATE_PRECISION }
          ).format({ symbol: "" })}%`
        : "-",
      // eslint-disable-next-line no-nested-ternary
      valueColor: fundingData
        ? Number(fundingData.funding_rate) >= 0
          ? COLORS.positive.one
          : COLORS.negative.one
        : TEXT_COLORS.one,
    };
    const fundingRateCountdown: IStat = {
      label: t("funding_rate_countdown"),
      value: fundingCountdown,
    };
    const dailyVolumeUSD: IStat = {
      label: t("daily_volume_usd"),
      value: statsData?.daily_volume
        ? currency(statsData.daily_volume).format()
        : "-",
    };
    const dailyVolumeAsset: IStat = {
      label: t("daily_volume", { asset: statsData?.asset || "" }),
      value: statsData?.daily_volume_contracts
        ? currency(statsData.daily_volume_contracts).format({
            symbol: "",
            precision: amount_precision,
          })
        : "-",
    };

    // FULL SCREEN
    const fullScreenStats: IStat[] = [];

    if (!perpInstrument?.pre_launch) {
      fullScreenStats.push(fundingRate);
      fullScreenStats.push(fundingRateCountdown);
    }

    const takerBuySellVolume: IStat = {
      label: t("buy_sell_volume"),
      value:
        statsData && Number(statsData.daily_volume) ? (
          <TakerBuySellVolumeIndicator
            buyPercentage={
              Number(statsData.daily_buy_volume) /
              Number(statsData.daily_volume)
            }
          />
        ) : (
          "-"
        ),
    };

    return [
      ...fullScreenStats,
      dailyVolumeAsset,
      dailyVolumeUSD,
      takerBuySellVolume,
    ];
  }, [
    amount_precision,
    fundingCountdown,
    fundingData,
    perpInstrument?.pre_launch,
    statsData,
    t,
  ]);

  return (
    <TradingHeaderWrapper isMobileScreen={isMobileScreen}>
      <OtherStatsWrapperParent>
        <OtherStatsWrapper isMobileScreen={isMobileScreen}>
          <AnimatePresence>
            <MarkPrice key="mark_price" perpInstrument={perpInstrument} />
            {!perpInstrument?.pre_launch && (
              <IndexPriceStat
                key="index_price"
                precision={price_precision}
                asset={perpInstrument?.underlying_asset}
              />
            )}
            <Mark24hChange
              key="mark_change"
              mark24hAgo={assetMark24hAgo}
              perpInstrument={perpInstrument}
            />
            {stats.map((stat) => (
              <Fragment key={String(stat.label)}>
                <Stat {...stat} />
              </Fragment>
            ))}
            <OpenInterestStat
              key="open_interest"
              openInterestContracts={Number(
                statsData?.open_interest?.total || 0
              )}
              perpInstrument={perpInstrument}
            />
          </AnimatePresence>
        </OtherStatsWrapper>
      </OtherStatsWrapperParent>
    </TradingHeaderWrapper>
  );
}

export default PerpsTradingHeader;
